/**
 * ColorRole - Role para conversão e manipulação de cores
 *
 * ComplexRole com 5 variantes:
 * - hex: string hexadecimal ("#ff0000", "#ff0000ff")
 * - rgb_object: objeto { r, g, b, a? }
 * - rgb_string: string "rgb(255, 0, 0)" ou "rgba(255, 0, 0, 0.5)"
 * - hsl_object: objeto { h, s, l, a? }
 * - hsl_string: string "hsl(0, 100%, 50%)" ou "hsla(0, 100%, 50%, 0.5)"
 *
 * @example
 * const color = new ColorRole();
 * color.convert('hex', 'rgb_object', '#ff0000')  // { r: 255, g: 0, b: 0 }
 * color.convert('rgb_object', 'hsl_string', { r: 255, g: 0, b: 0 })  // "hsl(0, 100%, 50%)"
 * color.cast('hex', 'red')                        // '#ff0000'
 * color.format('hex', '#ff0000', { uppercase: true })  // '#FF0000'
 */

import { ComplexRole } from "../../contracts";
import type { IVariant } from "../../contracts";
import type { RGBA, ColorFormatOptions } from "./types";
import {
  HexVariant,
  RgbObjectVariant,
  RgbStringVariant,
  HslObjectVariant,
  HslStringVariant,
} from "./variants";

// =============================================================================
// COLOR ROLE CLASS
// =============================================================================

export class ColorRole extends ComplexRole<RGBA> {
  readonly name = "color";
  readonly base = "rgb_object";

  protected createVariants(): Record<string, IVariant<unknown, RGBA>> {
    return {
      hex: new HexVariant(),
      rgb_object: new RgbObjectVariant(),
      rgb_string: new RgbStringVariant(),
      hsl_object: new HslObjectVariant(),
      hsl_string: new HslStringVariant(),
    };
  }

  /**
   * Override format to accept ColorFormatOptions
   */
  format(variant: string, value: unknown, options?: ColorFormatOptions): string {
    return super.format(variant, value, options);
  }
}

// =============================================================================
// SINGLETON INSTANCE
// =============================================================================

/** Instância singleton da ColorRole */
export const colorRole = new ColorRole();
